import React, { useEffect } from "react";

import NoSummary from "../no-summary";

import WasteSummaryLoader from "./WasteSummaryLoader";
import Cards from "./cards";
import LandFill from "./chart/LandFill";
import MaterialComposition from "./material-composition";
import ProjectDetails from "./project-details";
import RecoveryBreakdown from "./recovery-breakdown";
import ResourceRecovery from "./resource-recovery";
import WasteVolume from "./waste-volume";

import { useGetParams } from "@/hooks/useGetParams";
import useLocalPaginationHelper from "@/hooks/useLocalPaginationHelper";
import { usePagination } from "@/hooks/usePagination";
import { T_WasteManagementData } from "@/interfaces/customer-portal/waste-management.interface";
import { cn } from "@/lib/utils";
import RecoveryPathChart from "@/pages/customer-portal/waste-management/components/charts/RecoveryPathChart";
import { RecyclePercentage } from "@/pages/customer-portal/waste-management/components/charts/RecyclePercentage";
import {
  useGetSummaryProjectQuery,
  useGetSummaryReportQuery,
} from "@/redux/api/wasteManageSummaryandSettings";

interface IProps {
  setShowExport?: (value: boolean) => void;
  isExport?: boolean;
}

const WasteManageSummary: React.FC<IProps> = ({ isExport, setShowExport }) => {
  const filters = useGetParams([
    "startDate",
    "endDate",
    "customerId",
    "projectId",
  ]);
  const { setLocalPagination, paginationQuery } = useLocalPaginationHelper();

  const { data: summaryData, ...rest } = useGetSummaryReportQuery(
    filters.substring(1),
  );

  const { data: projectData, ...isProjectLoading } = useGetSummaryProjectQuery(
    filters.substring(1) + paginationQuery,
  );

  const pagination = usePagination(
    projectData?.pagination,
    10,
    setLocalPagination,
  );

  useEffect(() => {
    if (!summaryData) {
      if (setShowExport) {
        setShowExport(false);
      }
    } else {
      if (setShowExport) {
        setShowExport(true);
      }
    }
  }, [summaryData, setShowExport]);

  if (isProjectLoading.isLoading || rest.isLoading) {
    return <WasteSummaryLoader />;
  }

  if (!summaryData) {
    return <NoSummary />;
  }

  return (
    <>
      <ProjectDetails
        projectData={projectData?.data ?? []}
        pagination={pagination}
      />
      <RecoveryPathChart
        wasteManagementData={summaryData as T_WasteManagementData}
      />
      <LandFill />
      <div
        className={cn(
          "grid grid-cols-[0.6fr_0.4fr] gap-2",
          isExport && "grid grid-cols-1",
        )}
        id='pagebreak'
      >
        {isExport ? (
          <>
            <section className='w-full non-visible-table'>
              <MaterialComposition
                composition={summaryData?.wasteComposition as object}
              />
            </section>{" "}
            <section className='w-full non-visible-table'>
              <RecyclePercentage
                wasteManagementData={summaryData as T_WasteManagementData}
                className='h-[470px]'
              />
            </section>
          </>
        ) : (
          <>
            <section className='non-visible-table'>
              <MaterialComposition
                composition={summaryData?.wasteComposition as object}
              />
            </section>{" "}
            <section className='non-visible-table'>
              <RecyclePercentage
                wasteManagementData={summaryData as T_WasteManagementData}
                className='h-[470px]'
              />
            </section>
          </>
        )}
      </div>
      <WasteVolume wasteVolume={summaryData?.wasteVolume as object} />
      <section>
        <Cards
          recoveryPathway={
            summaryData?.recoveryPathway as {
              RECYCLE: number;
              REUSE: number;
              LANDFILL: number;
              ENERGYCONVERSION: number;
            }
          }
          noOfProjects={projectData?.data?.length || (0 as number)}
          noOfWasteType={
            (Object?.keys(summaryData?.wasteComposition || {})?.length ||
              0) as number
          }
          isExport
        />
      </section>

      <ResourceRecovery
        resourceRecoveryDetails={
          summaryData?.detailedWasteComposition as object
        }
      />
      <RecoveryBreakdown
        recoveryBreakDown={summaryData?.recoveryBreakDown as object}
      />
    </>
  );
};

export default WasteManageSummary;
